import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import ContentPreviewPopOver from '../components/ContentPreviewPopOver';
import type { Placement } from '../components/ContentPreviewPopOver';
import PostDetailPreview from './PostDetailPreview';

export type { Placement } from '../components/ContentPreviewPopOver';

interface PostDetailPreviewPopOverProps {
  postId: string;
  anchor?: string;
  children: React.ReactNode;
  offset?: number;
  onPreviewLoadChange?: (loaded: boolean) => void;
  controls?: {
    preventOpening?: boolean;
    open?: boolean;
    initialDelay?: number;
    loadedDelay?: number;
  };
  display?: 'block' | 'inline-block' | 'inline';
  allowCrossAxisFlip?: boolean;
  queryId?: string;
  resultId?: string;
  position?: number;
  placement?: Placement;
}

const PostDetailPreviewPopOver = ({
  postId,
  anchor,
  children,
  controls,
  offset,
  onPreviewLoadChange,
  display,
  allowCrossAxisFlip,
  queryId,
  resultId,
  position,
  placement,
}: PostDetailPreviewPopOverProps) => {
  const isControlled = Boolean(!_.isNil(controls?.open));
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

  useEffect(() => {
    if (!isControlled) {
      return () => undefined;
    }
    if (controls?.open) {
      // Loading the preview slows down the rending.
      // If preview is not yet loaded, open the popover only if "open" prop is true
      // for at least {initialDelay}ms
      // otherwise wait for {loadedDelay}ms to ignore fast mouse move.
      const timeout = setTimeout(
        () => {
          setIsOpen(true);
        },
        isPreviewLoaded
          ? (controls.loadedDelay ?? 0)
          : (controls.initialDelay ?? 0),
      );
      return () => clearTimeout(timeout);
    }

    setIsOpen(false);
    return () => undefined;
  }, [
    isControlled,
    controls?.open,
    isPreviewLoaded,
    controls?.initialDelay,
    controls?.loadedDelay,
  ]);

  const handleOnOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setIsOpen(newOpen);
      return;
    }
    if (!newOpen) {
      setIsOpen(false);
    }
  };

  const handleOnPreviewLoadChange = (loaded: boolean) => {
    onPreviewLoadChange?.(loaded);
    setIsPreviewLoaded(loaded);
  };

  return (
    <ContentPreviewPopOver
      display={display}
      hidden={!isPreviewLoaded}
      hideOnMobile
      allowCrossAxisFlip={allowCrossAxisFlip}
      offset={offset}
      minWidth={250}
      placement={placement}
      popOver={
        <PostDetailPreview
          postId={postId}
          anchor={anchor}
          onPreviewLoadChange={handleOnPreviewLoadChange}
          queryId={queryId}
          resultId={resultId}
          position={position}
          open={isOpen}
        />
      }
      open={!controls?.preventOpening && isOpen}
      onOpenChange={handleOnOpenChange}
    >
      {children}
    </ContentPreviewPopOver>
  );
};

export default PostDetailPreviewPopOver;
